import {
    AreaSelectionCircle,
    AreaSelectionFreehand,
    AreaSelectionOutputData,
    AreaSelectionRectangle,
    AreaSelectionShape,
    AreaSelectionType
} from '@he-novation/config/types/area-selection.types';
import { Freehand, NoteMetadata, Rectangle } from '@he-novation/config/types/note.medatata.types';

import { AreaSelectionAtom } from '$atoms/file-atoms/area-selection-atom';

type Point = {
    x: number;
    y: number;
};
export const normalizePoints = (sc: HTMLCanvasElement, points: Point[]) => {
    return points.map((p) => ({
        x: p.x / sc.width,
        y: p.y / sc.height
    }));
};

export const getBoundingRectangle = (points: Point[]) => {
    return points.reduce((acc, p) => {
        if (!acc[0] || p.x < acc[0]) acc[0] = p.x;
        if (!acc[1] || p.y < acc[1]) acc[1] = p.y;
        if (!acc[2] || p.x > acc[2]) acc[2] = p.x;
        if (!acc[3] || p.y > acc[3]) acc[3] = p.y;
        return acc;
    }, [] as number[]) as [number, number, number, number];
};

export const pointsToRectangle = (points: Point[]) => {
    const _boundingRectangle = getBoundingRectangle(points);
    return {
        x: _boundingRectangle[0],
        y: _boundingRectangle[1],
        width: _boundingRectangle[2] - _boundingRectangle[0],
        height: _boundingRectangle[3] - _boundingRectangle[1]
    };
};

export const rectangleToPoints = (rectangle: Rectangle) => {
    return [
        { x: rectangle.x, y: rectangle.y },
        { x: rectangle.x + rectangle.width, y: rectangle.y },
        { x: rectangle.x + rectangle.width, y: rectangle.y + rectangle.height },
        { x: rectangle.x, y: rectangle.y + rectangle.height }
    ];
};

export const circleToRectangle = (circle: AreaSelectionCircle) => {
    return {
        x: circle.x - circle.radiusToWidth,
        y: circle.y - circle.radiusToHeight,
        width: circle.radiusToWidth * 2,
        height: circle.radiusToHeight * 2
    };
};

export const getShapeOptions = (type?: AreaSelectionShape, bg?: boolean, color?: string) => {
    const obj: Partial<{
        rounded: boolean;
        renderPoints: boolean;
        isCircle: boolean;
        renderingContext: (c2d: CanvasRenderingContext2D) => void;
    }> = {};
    let lineWidth = 3;

    obj.renderPoints = false;

    if (type === AreaSelectionShape.Rectangle) {
        //background is only there on note add
        if (!bg) {
            obj.rounded = true;
            obj.renderPoints = false;
        } else {
            obj.renderPoints = true;
        }
    }

    if (type === AreaSelectionShape.Circle) {
        obj.isCircle = true;
        obj.renderPoints = false;
        obj.rounded = true;
    }

    obj.renderingContext = function (c2d) {
        c2d.lineWidth = lineWidth;
        c2d.shadowColor = '#000';
        c2d.shadowBlur = 10;
        c2d.shadowOffsetX = 1;
        c2d.shadowOffsetY = 1;
        //if (bg) c2d.setLineDash([8, 4]);
    };
    return obj;
};

export const mapMetadataToSelection = (metadata: NoteMetadata): AreaSelectionOutputData => {
    if ('drawing' in metadata && metadata.drawing) {
        return metadata.drawing;
    } else {
        if (metadata.freehand) {
            return {
                freehand: metadata.freehand?.map((f) => ({
                    points: f,
                    color: '#ff0000'
                })),
                bounds: {
                    ...metadata.rectangle!,
                    color: '#ff0000'
                }
            };
        } else {
            return {
                bounds: { ...metadata.rectangle!, color: '#ff0000' }
            };
        }
    }
};

export const mapAreaSelectionToMetadata = (state: AreaSelectionAtom): AreaSelectionOutputData => {
    if (state.type === AreaSelectionType.Multiple) {
        const freehand = state.selection
            .filter((s) => !!s.freehand)
            .map((s) => s.freehand) as AreaSelectionFreehand[];
        const circle = state.selection
            .filter((s) => !!s.circle)
            .map((s) => s.circle) as AreaSelectionCircle[];

        const rectangle = state.selection
            .filter((s) => !s.freehand && !s.circle)
            .map((s) => s.rectangle) as AreaSelectionRectangle[];

        const points = freehand
            .map((f) => f.points)
            .concat(
                circle.map((circle) =>
                    rectangleToPoints({ ...circleToRectangle(circle), color: circle.color })
                )
            )
            .concat(rectangle.map(rectangleToPoints));

        return {
            freehand,
            circle,
            rectangle,
            bounds: {
                ...pointsToRectangle(points.reduce((acc, curr) => [...acc, ...curr], [])),
                color: state.color
            }
        };
    }

    return {
        bounds: state.selection?.[0]?.rectangle
    };
};

export const mapMetadataToFreehandItems = (
    metadata: NoteMetadata
): { shapes: Freehand[]; bounds: Rectangle } => {
    if ('drawing' in metadata && metadata.drawing) {
        return {
            shapes: [
                ...(metadata.drawing.freehand || []).map((freehand) => ({
                    points: freehand.points,
                    color: freehand.color
                })),
                ...(metadata.drawing.rectangle ? metadata.drawing.rectangle : []).map(
                    (rectangle) => ({
                        points: [
                            { x: rectangle.x, y: rectangle.y },
                            { x: rectangle.x + rectangle.width, y: rectangle.y },
                            { x: rectangle.x + rectangle.width, y: rectangle.y + rectangle.height },
                            { x: rectangle.x, y: rectangle.y + rectangle.height }
                        ],
                        color: rectangle.color
                    })
                ),
                ...(metadata.drawing.circle ? metadata.drawing.circle : []).map((circle) => ({
                    points: [
                        { x: circle.x - circle.radiusToWidth, y: circle.y - circle.radiusToHeight },
                        { x: circle.x + circle.radiusToWidth, y: circle.y - circle.radiusToHeight },
                        { x: circle.x + circle.radiusToWidth, y: circle.y + circle.radiusToHeight },
                        { x: circle.x - circle.radiusToWidth, y: circle.y + circle.radiusToHeight }
                    ],
                    color: circle.color
                }))
            ],
            bounds: metadata.drawing.bounds
        };
    } else {
        return {
            shapes:
                metadata.freehand?.map((freehand) => ({
                    points: freehand,
                    color: '#ff0000'
                })) ?? [],
            bounds: {
                x: metadata.rectangle!.x,
                y: metadata.rectangle!.y,
                width: metadata.rectangle!.width,
                height: metadata.rectangle!.height,
                color: '#ff0000'
            }
        };
    }
};
